import AlertTriangle from '@crehana/compass.icons.outline/AlertTriangle';
import CloseSmall from '@crehana/compass.icons.outline/CloseSmall';
import Upload from '@crehana/compass.icons.outline/Upload';
import { InputMessage } from '@crehana/compass.ui/v3/input';
import { randomizer } from '@crehana/utils';
import { useState } from 'react';
import { InputFile } from '@/shared/components/input-file';
import { FileAttached } from '@/shared/components/input-file/components/file-attached';
import type { TAttachmentFile } from '@/shared/components/input-file/types';
import { ACCEPTED_ALL_FILE_EXTENSIONS } from '@/shared/components/input-file/utils';
import { useCreateFile } from '../hooks/use-create-file';
import { useAddResourceBundle } from '../i18n/use-add-resource-bundle';
import { useTranslationSharedCandidateForm } from '../i18n/use-translations';

type TUploadCandidateFormAttachments = {
  onUpdateAttachment: (attachment: number | undefined) => void;
  uploadId?: string;
  originalId: number;
  acceptFileTypes?: string;
};

const MAX_ATTACHMENTS = 1;
const MAX_SIZE_FILE = 5000;

// "  .pdf, .doc " -> "PDF, DOC" — usado en el mensaje de formato rechazado (AC-3).
const formatAcceptedExtensions = (acceptFileTypes: string) =>
  acceptFileTypes
    .split(',')
    .map(extension => extension.trim().replace('.', '').toUpperCase())
    .join(', ');

export const UploadCandidateFormAttachments: React.FC<
  TUploadCandidateFormAttachments
> = ({
  onUpdateAttachment,
  uploadId = 'default',
  originalId,
  acceptFileTypes = ACCEPTED_ALL_FILE_EXTENSIONS,
}) => {
  // WHY: se auto-registra el bundle (mismo patrón que
  // shared/components/documents/dropzone-upload-file/dropzone-upload-file.tsx:59) porque el
  // diálogo real de "Agregar candidato" nunca llamaba a
  // useAddResourceBundle() — sólo lo hacía el decorador de Storybook
  // (TCSTC-4720). Así queda cubierto cualquier consumidor presente o
  // futuro, sin depender de que cada pantalla se acuerde de registrarlo.
  useAddResourceBundle();
  const { t } = useTranslationSharedCandidateForm();
  const [attachmentFile, setAttachmentFile] = useState<
    Record<string, TAttachmentFile>
  >({});
  const [isErrorUploadFile, setIsErrorUploadFile] = useState<boolean>(false);
  const [isErrorInvalidFormat, setIsErrorInvalidFormat] =
    useState<boolean>(false);

  const { uploadMultipleFileToAws } = useCreateFile(originalId);
  const canAddMoreFiles = Object.keys(attachmentFile).length < MAX_ATTACHMENTS;

  const isFileSizeValid = (files: File[]) =>
    !files.some(file => file.size / 1024 > MAX_SIZE_FILE);
  const handleRemoveFile = (key: string) => {
    setAttachmentFile(prev => {
      const updated = { ...prev };
      delete updated[key];
      setIsErrorUploadFile(false);
      setIsErrorInvalidFormat(false);

      if (Object.keys(updated).length === 0) {
        onUpdateAttachment(undefined);
      }

      return updated;
    });
  };
  const updateAttachmentState = (
    key: string,
    changes: Partial<TAttachmentFile>,
  ) => {
    setAttachmentFile(prev => ({
      ...prev,
      [key]: { ...prev[key], ...changes },
    }));
  };
  const handleFileUpload = async (file: File, internalId: string) => {
    try {
      await uploadMultipleFileToAws(file, internalId, setAttachmentFile);
      setIsErrorUploadFile(false);
      setIsErrorInvalidFormat(false);

      // Verificar archivos subidos después de la carga
      const currentFiles = await new Promise<Record<string, TAttachmentFile>>(
        resolve => {
          setAttachmentFile(prevState => {
            resolve(prevState);
            return prevState;
          });
        },
      );

      const allFilesUploaded = Object.values(currentFiles).every(
        file => file.fileId && !file.isLoading,
      );

      if (allFilesUploaded) {
        const firstFileKey = Object.keys(currentFiles)[0];
        if (firstFileKey && currentFiles[firstFileKey].fileId) {
          onUpdateAttachment(currentFiles[firstFileKey].fileId);
        } else {
          onUpdateAttachment(undefined);
        }
      }
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      updateAttachmentState(internalId, { isLoading: false, isError: true });
    }
  };

  const handleRetryFile = (key: string) => {
    updateAttachmentState(key, { isLoading: true, isError: false });
    handleFileUpload(attachmentFile[key].file, key);
    setIsErrorUploadFile(false);
  };

  const initializeFilesUpload = (files: File[]) => {
    const newFiles = files.filter(
      file =>
        !Object.values(attachmentFile).some(f => f.file.name === file.name),
    );
    const attachments = newFiles.reduce(
      (acc, file) => {
        const id = randomizer.getString();
        acc[id] = {
          file,
          progress: { progress: 0, remainingSeconds: 0, uploadSpeed: 0 },
          isLoading: true,
          isError: false,
        };
        return acc;
      },
      {} as Record<string, TAttachmentFile>,
    );
    setIsErrorUploadFile(false);
    setIsErrorInvalidFormat(false);
    setAttachmentFile(prev => ({ ...prev, ...attachments }));

    return attachments;
  };

  // WHY: los dos mensajes describen el último archivo elegido — sin apagar el
  // otro, un PDF pesado después de un .docx seguía diciendo «formato no
  // permitido» junto al error de peso (TCSTC-4720).
  const handleSizeError = (isError: boolean) => {
    setIsErrorUploadFile(isError);
    if (isError) {
      setIsErrorInvalidFormat(false);
    }
  };
  const handleInvalidFormat = () => {
    setIsErrorUploadFile(false);
    setIsErrorInvalidFormat(true);
  };

  const handleFileSelection = async (fileList: FileList) => {
    const files = Array.from(fileList);
    if (files.length <= MAX_ATTACHMENTS && isFileSizeValid(files)) {
      const newAttachments = initializeFilesUpload(files);
      await Promise.all(
        Object.entries(newAttachments).map(([id, attachment]) =>
          handleFileUpload(attachment.file, id),
        ),
      );
      return;
    }
    setIsErrorUploadFile(false);
  };
  return (
    <main className="tw-flex tw-flex-col tw-w-full tw-px-0 tw-mt-0.8">
      {Object.entries(attachmentFile).map(
        ([
          key,
          {
            file: { name, size },
            progress,
            isLoading,
            isError,
          },
        ]) => (
          <FileAttached
            key={key}
            name={name}
            size={size}
            progress={progress?.progress}
            isLoading={isLoading}
            isError={isError}
            onClick={() => handleRemoveFile(key)}
            handleRetry={() => handleRetryFile(key)}
            IconDelete={CloseSmall}
          />
        ),
      )}
      {isErrorUploadFile && (
        <article className="tw-w-full tw-pb-2.4">
          <div className="tw-flex tw-items-center">
            <AlertTriangle size={20} className="tw-text-alert-400 tw-mr-0.8" />
            <InputMessage error>{t('FILE_EXCEEDS_LIMIT')}</InputMessage>
          </div>
        </article>
      )}
      {isErrorInvalidFormat && (
        <article className="tw-w-full tw-pb-2.4">
          <div className="tw-flex tw-items-center">
            <AlertTriangle size={20} className="tw-text-alert-400 tw-mr-0.8" />
            <InputMessage error>
              {t('UPLOAD_ATTACHMENT_INVALID_FORMAT', {
                interpolation: {
                  formats: formatAcceptedExtensions(acceptFileTypes),
                  maxSize: `${MAX_SIZE_FILE / 1000}MB`,
                },
              })}
            </InputMessage>
          </div>
        </article>
      )}
      {canAddMoreFiles && (
        <article className="tw-flex tw-gap-1.2 tw-items-center tw-w-full tw-mt-0.8">
          <div className="tw-w-[17rem] tw-h-3.2">
            <InputFile
              id={`inputFile_${uploadId}`}
              maxSize={MAX_SIZE_FILE}
              label={
                <div className="tw-btn-primary-xs tw-btn-icon">
                  <Upload
                    size={16}
                    className="tw-text-current group-hover:tw-text-primary-light-500"
                  />
                  {t('UPLOAD_ATTACHMENT')}
                </div>
              }
              hasNoPreview
              onLoadFile={file => {
                handleFileSelection(file as FileList);
              }}
              accept={acceptFileTypes}
              isMultiple
              isErrorUploadFile={handleSizeError}
              onInvalidFileFormat={handleInvalidFormat}
              uploadButtonAlign="start"
            />
          </div>
        </article>
      )}
    </main>
  );
};
